import { useState, useEffect, useRef, Fragment } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useAlert } from "../providers/AlertService";
import CreatableSelect from "react-select/creatable";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import useCachedForm from "../hooks/useCachedForm";
import useResizablePage from "../hooks/useResizablePage";
import Checkbox from "./Checkbox";
import Input from "./Input";
import QrButton from "./Button";
import SignatureModal from "./SignatureModal";
import useAppointment from "../hooks/useAppointment";
import useUser from "../hooks/useUser";
import useZoomScale from "../hooks/useZoomScale";
import BackButton from "components/BackButton";
import ScaledButton from "components/ScaledButton";

import {
  generateDefaultValues,
  generateRefs,
  generateErrorMessages,
} from "../utils/form-tools";
import { set } from "date-fns";

const MobileForm = ({
  type,
  version,
  fields,
  useValidation = () => ({}),
  background,
  width,
  height,
  onDoMath,
}) => {
  const history = useHistory();
  const { appointmentId } = useParams();
  // console.log({ appointmentId })
  const { appointment } = useAppointment(appointmentId);
  const { user } = useUser();

  const defaultValues = generateDefaultValues(fields);
  const signatureRefs = generateRefs(fields);
  const isMounted = useRef(false);
  const scale = useZoomScale();


  const {
    register,
    handleSubmit,
    reset,
    control,
    errors,
    clearErrors,
    setValue,
    getValues,
  } = useForm({
    defaultValues,
  });

  // console.log({
  //   appointmentId,
  //   type,
  //   version,
  //   defaultValues,
  //   reset,
  //   isMounted: isMounted.current
  // })

  const { cacheFormValues } = useCachedForm({
    appointmentId,
    type,
    version,
    defaultValues,
    reset,
    isMounted: isMounted.current
  });


  const flushAllToCache = async () => {
    const values = getValues();

    for (const [name, value] of Object.entries(values)) {
      if (value === undefined) continue; // don't overwrite
      await cacheFormValues(name, value);
    }
  };

  const handleDoMathClick = async () => {
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }

    await flushAllToCache();

    if (onDoMath) {
      await onDoMath();
    }
  };

  const { page } = useResizablePage({ width, height });
  const widthRatio = (page.width < 1280 && page.width >= 600) ? 0.8 : 1; // kind of dirty hack
  const heightRatio = (page.width < 1280 && page.width >= 600) ? 0.7 : 1; // kind of dirty hack
  const logic = useValidation(control);

  const alert = useAlert();

  const [modalSignatureFieldName, setModalSignatureFieldName] = useState(null);
  const reviewForm = async () => {
    // After sending the server the data, mark it as reviewed
    await cacheFormValues('reviewed', 1);
    alert({ description: 'success' }).then(() =>
      history.push(`/appointment/${appointmentId}`)
    );
    /*
    api.post(`/forms/${appointmentId}/revision`, {
      fields: data,
      appointmentId,
      enrollmentNumber: appointment.customer.enrollment.enrollmentNumber,
      type,
      revision: Number(forms.length) + 1,
      version
    })
     */
  };
  // fill in automatical values
  const setEnrollmentInformation = () => {
    // if (!appointment) return;
    const customer = appointment.customer;
    if (customer) {
      const address = `${customer.streetNumber} ${customer.streetName}, ${customer.city} CA ${customer.zip}`;
      const customerName = `${customer.firstName}` + `${customer.middleName ? ` ${customer.middleName} ` : " "}` + `${customer.lastName}`;
      setValueIfEmpty('applicantName', customerName);
      setValueIfEmpty('enrollmentNumber', customer.enrollment?.enrollmentNumber);
      setValueIfEmpty('fullAddress', address);
      setValueIfEmpty('enrollmentAddress', address);
      setValueIfEmpty('applicantAddress', address);
      setValueIfEmpty('installationAddress', address);
      setValueIfEmpty('address', address);
      setValueIfEmpty('cityZip', `${customer.city}/${customer.zip}`);
      setValueIfEmpty('customerName', customerName);
      setValueIfEmpty('customer.name', customerName)
      setValueIfEmpty('climateZone', customer.climateZone)
      setValueIfEmpty('owner', (customer.occupancyStatus === "Owner"))
      setValueIfEmpty('renter', (customer.occupancyStatus === "Renter"))
    }
    setValueIfEmpty('contractorName', user.company?.name);
    setValueIfEmpty('contractor', user.company?.name);
    setValueIfEmpty('contractorPhone', user.company?.phone);
    const technician = appointment.technician;
    if (technician) {
      const technicianName = `${technician.firstName} ${technician.lastName}`;
      setValueIfEmpty('installerName', technicianName);
      setValueIfEmpty('technicianName', technicianName);
      setValueIfEmpty('technician', technicianName);
    }

    setValueIfEmpty('newBrand', "LG");
    setValueIfEmpty('newModelNumber', "WT7150CW");
    setValueIfEmpty('customer.date', new Date().toLocaleDateString());
    setValueIfEmpty('customerDate', new Date().toLocaleDateString());
    setValueIfEmpty('date', new Date().toLocaleDateString());
    setValueIfEmpty('assessorDate', new Date().toLocaleDateString());
    setValueIfEmpty('technicianDate', new Date().toLocaleDateString());
    setValueIfEmpty('applicantDate', new Date().toLocaleDateString());
    setValueIfEmpty('installerDate', new Date().toLocaleDateString());
    setValueIfEmpty('visitDate', new Date().toLocaleDateString());
    setValueIfEmpty('date', new Date().toLocaleDateString());
    setValueIfEmpty('customerRefusedToSign.date', new Date().toLocaleDateString());
    setValueIfEmpty('customerRefusedToSign.time', new Date().toLocaleTimeString());
  }

  useEffect(() => {
    if (appointment) {
      const signatureFields = Object.keys(fields).filter(
        (key) => fields[key].needsRef
      );

      for (const field of signatureFields) {
        if (signatureRefs[field]) {
          const signatureFieldValue = getValues(field);
          if (signatureFieldValue)
            signatureRefs[field].src = signatureFieldValue;
        }
      }

      setEnrollmentInformation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, appointment, user]);

  // alert errors on validation
  useEffect(() => {
    if (Object.keys(errors).length) {
      const errorMessages = generateErrorMessages(errors);
      alert({ description: errorMessages.join("<br />") });
      clearErrors();
    }
  }, [errors, clearErrors]);

  useEffect(() => {
    isMounted.current = true;

    return () => isMounted.current = false;
  }, []);


  useEffect(() => {
    if (sessionStorage.getItem("reloadTwice") === "true") {
      sessionStorage.removeItem("reloadTwice"); // Clean up to avoid infinite loop
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }, []);

  const setValueIfEmpty = (fieldName, value) => {
    if (!getValues(fieldName)) {
      setValue(fieldName, value);
      cacheFormValues(fieldName, value);
    }
  }

  const handleQrReader = fieldName => {
    history.push(`/scan/${appointmentId}/${type}/${version}/${fieldName}`)
  }

  const renderValue = (value, options) => {
    const val = value === "" ?
      null :
      options.find((obj) => obj.value === value);
    return val || { value: value, label: value };
  };
  return (
    <>
      <Box display="flex" m={1}>
        <BackButton url={`/appointment/${appointmentId}`} />
        {/* {!!history.location.state && 
        <BackButton url={history.location.state.prevPath}/>} */}
        <Box ml={1}>
          <ScaledButton color="primary" variant="contained" onClick={handleSubmit(reviewForm)}>Review</ScaledButton>
        </Box>
        <Box ml={1}>
          <ScaledButton
            color="primary"
            variant="contained"
            // onClick={handleDoMathClick}
            onClick={() => {
              sessionStorage.setItem("reloadTwice", "true");
              window.location.reload();
            }}
          >
            Do Math
          </ScaledButton>
        </Box>
      </Box>
      <form
        style={{
          fontSize: `${Math.max(8, page.width / 100)}px`,
          position: "relative",
          width: page.width,
          height: page.height,
          backgroundImage: `url(${background})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* eslint-disable-next-line array-callback-return */}
        {Object.values(fields).map((field) => {
          const style = {
            position: "absolute",
            top: field.yMod * page.height,
            left: field.xMod * page.width,
            height: ["text", "number", "textarea"].includes(field.type) ? field.heightMod * page.height * heightRatio : field.heightMod * page.height,
            width: ["text", "number", "textarea"].includes(field.type) ? field.widthMod * page.width * widthRatio : field.widthMod * page.width,
            outline: "none",
            border: "none",
            background: "none",
            fontSize: `8px`,
            // fontSize: `${widthRatio}rem / 10`,
          };

          // eslint-disable-next-line default-case
          switch (field.type) {
            case "serial":
              return (
                <QrButton
                  onClick={() => handleQrReader(field.name)}
                  key={field.name}
                  name={field.name}
                  disabled={field.readonly}
                  field={field}
                  page={page}
                  onBlur={(event) =>
                    cacheFormValues(event.target.name, event.target.value)
                  }
                  control={control}
                  ref={(ref) => {
                    if (field.name in logic) {
                      register(ref, logic[field.name]());
                    } else {
                      register(ref);
                    }
                  }}
                >
                  Scan#{field.name === 'smartThermostat.scanSerial1' ? '1' : '2'}
                </QrButton>
              );
            case "text":
              return (
                <Input
                  fontSize='5px'
                  key={field.name}
                  name={field.name}
                  disabled={field.readonly}
                  field={field}
                  page={page}
                  onBlur={(event) =>
                    cacheFormValues(event.target.name, event.target.value)
                  }
                  control={control}
                  ref={(ref) => {
                    if (field.name in logic) {
                      register(ref, logic[field.name]());
                    } else {
                      register(ref);
                    }
                  }}
                />
              );
            case "number":
              return (
                <Input
                  key={field.name}
                  name={field.name}
                  field={field}
                  page={page}
                  type="number"
                  onBlur={(event) =>
                    cacheFormValues(event.target.name, event.target.value)
                  }
                  control={control}
                  ref={(ref) => {
                    if (field.name in logic) {
                      register(ref, logic[field.name]());
                    } else {
                      register(ref);
                    }
                  }}
                />
              );
            case "textarea":
              return (
                <textarea
                  key={field.name}
                  id={field.name}
                  name={field.name}
                  ref={(ref) => {
                    if (field.name in logic) {
                      register(ref, logic[field.name]());
                    } else {
                      register(ref);
                    }
                  }}
                  style={{ ...style, lineHeight: field.lineHeight }}
                  control={control}
                  onChange={(event) =>
                    cacheFormValues(event.target.name, event.target.value)
                  }
                  {...style}
                />
              );
            case "select":
              return (
                <select
                  key={field.name}
                  style={style}
                  name={field.name}
                  onChange={(event) =>
                    cacheFormValues(event.target.name, event.target.value)
                  }
                  ref={(ref) => {
                    if (field.name in logic) {
                      register(ref, logic[field.name]());
                    } else {
                      register(ref);
                    }
                  }}
                >
                  <option></option>
                  {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              );
            case "expandableSelect":
              return (
                <Controller
                  name={field.name}
                  control={control}
                  defaultValue=''
                  key={field.name}
                  render={({ onChange, value, ref }) => {
                    return <CreatableSelect
                      styles={{
                        container: () => style,
                        control: (provided) => ({
                          ...provided,
                          borderWidth: 0
                        })
                      }}
                      inputRef={ref}
                      placeholder=""
                      name={field.name}
                      value={renderValue(value, field.options)}
                      onChange={(newValue) => {
                        if (newValue) {
                          setValue(field.name, newValue.value);
                          cacheFormValues(field.name, newValue.value);
                          onChange(newValue.value)
                        }
                      }}
                      isClearable
                      options={field.options}
                    />
                  }}
                />

              );
            case "checkbox":
              return (
                <Fragment key={field.name}>
                  <label
                    htmlFor={field.name}
                    style={{
                      position: "absolute",
                      width: field.label.widthMod * page.width,
                      height: field.label.heightMod * page.height,
                      left: field.label.xMod * page.width,
                      top: field.label.yMod * page.height,
                    }}
                  ></label>
                  <Checkbox
                    id={field.name}
                    readonly={field.readonly}
                    name={field.name}
                    ref={(ref) => {
                      if (field.name in logic) {
                        register(ref, logic[field.name]());
                      } else {
                        register(ref);
                      }
                    }}
                    control={control}
                    onChange={(event) =>
                      cacheFormValues(event.target.name, event.target.value)
                    }
                    {...style}
                  />
                </Fragment>
              );
            case "signature":
              return (
                <div
                  key={field.name}
                  style={{
                    position: "absolute",
                    width: field.widthMod * page.width,
                    height: field.heightMod * page.height,
                    left: field.xMod * page.width,
                    top: field.yMod * page.height,
                  }}>
                  <img
                    ref={(ref) => {
                      signatureRefs[field.name] = ref;
                    }}
                    style={{
                      width: '100%',
                      height: '100%'
                    }}
                    alt={field.name}
                    onClick={() => setModalSignatureFieldName(field.name)}
                  />
                  <input
                    id={field.name}
                    name={field.name}
                    ref={(ref) => {
                      if (field.name in logic) {
                        register(ref, logic[field.name]());
                      } else {
                        register(ref);
                      }
                    }}
                    control={control}
                    type="hidden"
                  />
                </div>
              );
          }
        })}
      </form>
      {!!modalSignatureFieldName && (
        <SignatureModal
          fieldName={modalSignatureFieldName}
          setFieldName={setModalSignatureFieldName}
          setValue={setValue}
          getValues={getValues}
          cacheFormValues={cacheFormValues}
        />
      )}
    </>
  );
};

export default MobileForm;
